import type { CommerceCalculationRuntimeItem } from "../gateway/local-projection-client";
import {
  captureCurrentCartRuntimeToken,
  currentCartRuntimeScopeKey,
  isCurrentCartRuntimeTokenCurrent,
  publishCurrentCartRuntime,
  publishCurrentCartRuntimeIfCurrent,
  readCurrentCartRuntime,
} from "./current-cart-runtime";
import type { TrustedTerminalIdentity } from "./terminal-state";
import { posRuntimeDb } from "./pos-db";

export type LocalCartModifier = {
  modifierGroupId: string;
  modifierGroupName: string;
  modifierOptionId: string;
  modifierOptionName: string;
  quantity: number;
  priceDelta: number;
  priceVersionId: string;
};

export type LocalCartLine = {
  lineId: string;
  menuItemId: string;
  menuVariantId: string | null;
  menuCategoryId: string | null;
  variantOptionIds: string[];
  menuName: string;
  variantLabel: string;
  quantity: number;
  baseUnitPrice: number;
  unitPrice: number;
  currencyCode: string;
  currencyScale: number | null;
  priceVersionId: string;
  modifiers: LocalCartModifier[];
};

export type LocalCartLineInput = Omit<LocalCartLine, "lineId" | "quantity">;

export type LocalSaleMember = {
  customerId: string;
  membershipId: string;
  customerCode: string;
  customerName: string;
  phone: string | null;
};

export type LocalPromotionLineAllocation = {
  lineId: string;
  amount: number;
  allocationPrecision: "LINE";
};

export type LocalPromotionApplicationCoverage = {
  ready: boolean;
  scope: "ORDER" | "UNIT";
  benefitCode: string;
  appliedApplicationCount: number;
  coveredUnits: Array<{
    lineId: string;
    unitNos: number[];
    coveredQuantity: number;
  }>;
};

export type LocalAppliedPromotion = {
  promotionPolicyId: string;
  promotionVersionId: string;
  policyCode: string;
  policyName: string;
  activationCode: string;
  appliedDiscountAmount: number;
  loyaltyEarnTreatment?: "FULL_POINTS" | "HALF_POINTS" | "NO_POINTS" | string;
  conditionDocument?: Record<string, unknown>;
  benefitDocument?: Array<{ code: string; params?: Record<string, unknown> }>;
  applicationCoverage?: LocalPromotionApplicationCoverage;
  lineAllocations?: LocalPromotionLineAllocation[];
  offlineCapability?: string;
  stackingMode?: "STACKABLE" | "EXCLUSIVE" | "BEST_DEAL" | string;
  stackGroupCode?: string | null;
};

export type LocalStaffClaimPromotion = {
  promotionPolicyId: string;
  promotionVersionId: string;
  policyCode: string;
  policyName: string;
  offlineCapability: string;
};

export type LocalSaleQuote = {
  cartFingerprint: string;
  quoteFingerprint: string;
  evaluatedAt: string;
  currencyCode: string;
  prePromotionTotal: number;
  promotionDiscountTotal: number;
  postPromotionTotal: number;
  appliedPromotions: LocalAppliedPromotion[];
  staffClaimOptions: LocalStaffClaimPromotion[];
  promotionRuntimeRevision?: number;
  quoteSource?: "LOCAL_PROMOTION_RUNTIME_V1" | "SERVER_COMMERCE_QUOTE_V1";
  requiresOnlineResolution?: boolean;
};

export type LocalCartDraft = {
  localCartId: string;
  requestKey: string;
  lines: LocalCartLine[];
  selectedMember: LocalSaleMember | null;
  claimedPromotionVersionIds: string[];
  quote: LocalSaleQuote | null;
  commerceCalculation: CommerceCalculationRuntimeItem | null;
  updatedAt: string;
};

let cartPersistenceQueue: Promise<void> = Promise.resolve();

function cartScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function cartLocalId(identity: TrustedTerminalIdentity) {
  return `cart:${cartScope(identity)}`;
}

function currentRuntimeCart(identity: TrustedTerminalIdentity) {
  const runtime = readCurrentCartRuntime();
  return runtime.scopeKey === currentCartRuntimeScopeKey(identity) ? runtime.cart : null;
}

function currentRuntimeCartForDraft(cart: LocalCartDraft) {
  const runtime = readCurrentCartRuntime();
  return runtime.cart?.localCartId === cart.localCartId ? runtime.cart : cart;
}

function queueCartPersistence<T>(operation: () => Promise<T>): Promise<T> {
  const run = cartPersistenceQueue.catch(() => undefined).then(operation);
  cartPersistenceQueue = run.then(() => undefined, () => undefined);
  return run;
}

function newRequestKey() {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `pos-cart:${id}`;
}

function resetEmptyCartContext(cart: LocalCartDraft): LocalCartDraft {
  return {
    ...cart,
    requestKey: newRequestKey(),
    lines: [],
    selectedMember: null,
    claimedPromotionVersionIds: [],
    quote: null,
    commerceCalculation: null,
  };
}

function normalizeStringIds(value: unknown) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((entry): entry is string => typeof entry === "string" && Boolean(entry)))].sort();
}

function normalizeModifiers(value: unknown): LocalCartModifier[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is LocalCartModifier => {
    if (!entry || typeof entry !== "object") return false;
    const item = entry as Partial<LocalCartModifier>;
    return (
      typeof item.modifierGroupId === "string" &&
      typeof item.modifierGroupName === "string" &&
      typeof item.modifierOptionId === "string" &&
      typeof item.modifierOptionName === "string" &&
      typeof item.quantity === "number" &&
      item.quantity > 0 &&
      typeof item.priceDelta === "number" &&
      Number.isFinite(item.priceDelta) &&
      typeof item.priceVersionId === "string"
    );
  });
}

function recoverBaseUnitPrice(candidate: Partial<LocalCartLine>, modifiers: LocalCartModifier[]) {
  if (typeof candidate.baseUnitPrice === "number" && Number.isFinite(candidate.baseUnitPrice)) {
    return candidate.baseUnitPrice;
  }
  if (typeof candidate.unitPrice !== "number" || !Number.isFinite(candidate.unitPrice)) return 0;
  const modifierTotal = modifiers.reduce(
    (sum, modifier) => sum + modifier.priceDelta * modifier.quantity,
    0,
  );
  const recovered = candidate.unitPrice - modifierTotal;
  return Number.isFinite(recovered) && recovered >= 0 ? recovered : 0;
}

function normalizeMember(value: unknown): LocalSaleMember | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Partial<LocalSaleMember>;
  if (!item.customerId || !item.membershipId || !item.customerCode || !item.customerName) return null;
  return {
    customerId: item.customerId,
    membershipId: item.membershipId,
    customerCode: item.customerCode,
    customerName: item.customerName,
    phone: typeof item.phone === "string" ? item.phone : null,
  };
}

function normalizeQuote(value: unknown): LocalSaleQuote | null {
  if (!value || typeof value !== "object") return null;
  const quote = value as Partial<LocalSaleQuote>;
  if (
    typeof quote.cartFingerprint !== "string" || !quote.cartFingerprint ||
    typeof quote.quoteFingerprint !== "string" || !quote.quoteFingerprint ||
    typeof quote.evaluatedAt !== "string" ||
    typeof quote.currencyCode !== "string" ||
    typeof quote.prePromotionTotal !== "number" || !Number.isFinite(quote.prePromotionTotal) ||
    typeof quote.promotionDiscountTotal !== "number" || !Number.isFinite(quote.promotionDiscountTotal) ||
    typeof quote.postPromotionTotal !== "number" || !Number.isFinite(quote.postPromotionTotal)
  ) return null;
  return {
    cartFingerprint: quote.cartFingerprint,
    quoteFingerprint: quote.quoteFingerprint,
    evaluatedAt: quote.evaluatedAt,
    currencyCode: quote.currencyCode,
    prePromotionTotal: quote.prePromotionTotal,
    promotionDiscountTotal: quote.promotionDiscountTotal,
    postPromotionTotal: quote.postPromotionTotal,
    appliedPromotions: Array.isArray(quote.appliedPromotions) ? quote.appliedPromotions : [],
    staffClaimOptions: Array.isArray(quote.staffClaimOptions) ? quote.staffClaimOptions : [],
    promotionRuntimeRevision: typeof quote.promotionRuntimeRevision === "number" ? quote.promotionRuntimeRevision : undefined,
    quoteSource: quote.quoteSource === "LOCAL_PROMOTION_RUNTIME_V1" || quote.quoteSource === "SERVER_COMMERCE_QUOTE_V1" ? quote.quoteSource : undefined,
    requiresOnlineResolution: typeof quote.requiresOnlineResolution === "boolean" ? quote.requiresOnlineResolution : undefined,
  };
}

function normalizeCommerceCalculation(value: unknown): CommerceCalculationRuntimeItem | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Partial<CommerceCalculationRuntimeItem>;
  if (
    typeof item.settings_version_id !== "string" || !item.settings_version_id ||
    typeof item.version_no !== "number" || !Number.isInteger(item.version_no) || item.version_no <= 0 ||
    item.rounding_scope !== "ALL_PAYMENTS" ||
    !["NONE", "NEAREST", "DOWN", "UP"].includes(item.rounding_mode ?? "") ||
    typeof item.rounding_increment !== "number" || !Number.isFinite(item.rounding_increment) || item.rounding_increment <= 0
  ) return null;
  return {
    settings_version_id: item.settings_version_id,
    version_no: item.version_no,
    rounding_scope: "ALL_PAYMENTS",
    rounding_mode: item.rounding_mode as CommerceCalculationRuntimeItem["rounding_mode"],
    rounding_increment: item.rounding_increment,
    effective_from: typeof item.effective_from === "string" ? item.effective_from : null,
    effective_to: typeof item.effective_to === "string" ? item.effective_to : null,
    changed_at: typeof item.changed_at === "string" ? item.changed_at : new Date(0).toISOString(),
  };
}

export function localCartSaleFingerprint(cart: Pick<LocalCartDraft, "lines" | "selectedMember" | "claimedPromotionVersionIds">) {
  return JSON.stringify({
    lines: cart.lines.map((line) => ({
      lineId: line.lineId,
      menuItemId: line.menuItemId,
      menuVariantId: line.menuVariantId,
      menuCategoryId: line.menuCategoryId,
      variantOptionIds: [...line.variantOptionIds].sort(),
      quantity: line.quantity,
      baseUnitPrice: line.baseUnitPrice,
      unitPrice: line.unitPrice,
      currencyCode: line.currencyCode,
      currencyScale: line.currencyScale,
      priceVersionId: line.priceVersionId,
      modifiers: line.modifiers
        .map((modifier) => ({ id: modifier.modifierOptionId, quantity: modifier.quantity, priceVersionId: modifier.priceVersionId }))
        .sort((a, b) => a.id.localeCompare(b.id)),
    })),
    member: cart.selectedMember ? { customerId: cart.selectedMember.customerId, membershipId: cart.selectedMember.membershipId } : null,
    claims: [...cart.claimedPromotionVersionIds].sort(),
  });
}

export async function readLocalCart(identity: TrustedTerminalIdentity): Promise<LocalCartDraft> {
  const localId = cartLocalId(identity);
  const record = await posRuntimeDb.journal.get(localId);
  if (!record || record.kind !== "CART_DRAFT" || !record.snapshot || typeof record.snapshot !== "object") {
    return {
      localCartId: localId,
      requestKey: newRequestKey(),
      lines: [],
      selectedMember: null,
      claimedPromotionVersionIds: [],
      quote: null,
      commerceCalculation: null,
      updatedAt: new Date().toISOString(),
    };
  }

  const snapshot = record.snapshot as Partial<LocalCartDraft>;
  const lines = Array.isArray(snapshot.lines)
    ? snapshot.lines.flatMap((line) => {
        if (!line || typeof line !== "object") return [];
        const candidate = line as Partial<LocalCartLine>;
        if (
          typeof candidate.lineId !== "string" || typeof candidate.menuItemId !== "string" ||
          typeof candidate.menuName !== "string" || typeof candidate.quantity !== "number" || candidate.quantity <= 0 ||
          typeof candidate.unitPrice !== "number" || !Number.isFinite(candidate.unitPrice) || typeof candidate.priceVersionId !== "string"
        ) return [];
        const modifiers = normalizeModifiers(candidate.modifiers);
        return [{
          ...candidate,
          menuVariantId: candidate.menuVariantId ?? null,
          menuCategoryId: typeof candidate.menuCategoryId === "string" ? candidate.menuCategoryId : null,
          variantOptionIds: normalizeStringIds(candidate.variantOptionIds),
          variantLabel: candidate.variantLabel ?? "BASE",
          currencyCode: candidate.currencyCode ?? "THB",
          currencyScale: typeof candidate.currencyScale === "number" && Number.isInteger(candidate.currencyScale) && candidate.currencyScale >= 0 && candidate.currencyScale <= 6 ? candidate.currencyScale : null,
          baseUnitPrice: recoverBaseUnitPrice(candidate, modifiers),
          modifiers,
        } as LocalCartLine];
      })
    : [];

  return {
    localCartId: localId,
    requestKey: typeof snapshot.requestKey === "string" && snapshot.requestKey ? snapshot.requestKey : record.requestKey,
    lines,
    selectedMember: normalizeMember(snapshot.selectedMember),
    claimedPromotionVersionIds: Array.isArray(snapshot.claimedPromotionVersionIds)
      ? snapshot.claimedPromotionVersionIds.filter((value): value is string => typeof value === "string" && Boolean(value))
      : [],
    quote: normalizeQuote(snapshot.quote),
    commerceCalculation: normalizeCommerceCalculation(snapshot.commerceCalculation),
    updatedAt: typeof snapshot.updatedAt === "string" ? snapshot.updatedAt : record.updatedAt,
  };
}

async function persistLocalCartNow(identity: TrustedTerminalIdentity, cart: LocalCartDraft) {
  const now = new Date().toISOString();
  const next: LocalCartDraft = { ...cart, updatedAt: now };
  const existing = await posRuntimeDb.journal.get(cartLocalId(identity));
  await posRuntimeDb.journal.put({
    localId: cartLocalId(identity),
    scopeKey: cartScope(identity),
    kind: "CART_DRAFT",
    state: "OPEN",
    requestKey: next.requestKey,
    snapshot: next,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  });
  return next;
}

export function saveLocalCart(identity: TrustedTerminalIdentity, cart: LocalCartDraft) {
  return queueCartPersistence(() => persistLocalCartNow(identity, cart));
}

async function mutateCurrentCart(
  identity: TrustedTerminalIdentity,
  fallback: LocalCartDraft,
  mutate: (cart: LocalCartDraft) => LocalCartDraft,
) {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const token = captureCurrentCartRuntimeToken(identity);
    const base = currentRuntimeCart(identity) ?? fallback;
    const next = mutate(base);
    if (!publishCurrentCartRuntimeIfCurrent(identity, next, token)) continue;

    const publishedToken = captureCurrentCartRuntimeToken(identity);
    const saved = await saveLocalCart(identity, next);
    publishCurrentCartRuntimeIfCurrent(identity, saved, publishedToken);
    return saved;
  }
  throw new Error("POS_CURRENT_CART_MUTATION_CONFLICT");
}

export async function bindLocalCartCommerceCalculation(
  identity: TrustedTerminalIdentity,
  cart: LocalCartDraft,
  settings: CommerceCalculationRuntimeItem,
) {
  const current = currentRuntimeCart(identity) ?? cart;
  if (!current.lines.length || current.commerceCalculation) return current;
  return mutateCurrentCart(identity, current, (base) => ({ ...base, commerceCalculation: settings }));
}

function modifierSignature(modifiers: LocalCartModifier[]) {
  return modifiers.map((modifier) => `${modifier.modifierOptionId}=${modifier.quantity}`).sort().join(",");
}

export function localCartLineId(line: LocalCartLineInput) {
  const signature = modifierSignature(line.modifiers);
  return `${line.menuItemId}:${line.menuVariantId ?? "BASE"}:${signature || "NO_MODIFIERS"}`;
}

export function applyLocalCartLine(
  cart: LocalCartDraft,
  line: LocalCartLineInput,
  commerceCalculation: CommerceCalculationRuntimeItem | null = null,
) {
  const sharedCart = currentRuntimeCartForDraft(cart);
  const activeCart = sharedCart.lines.length
    ? sharedCart
    : { ...resetEmptyCartContext(sharedCart), selectedMember: sharedCart.selectedMember };
  const lineId = localCartLineId(line);
  const existing = activeCart.lines.find((item) => item.lineId === lineId);
  const lines = existing
    ? activeCart.lines.map((item) => item.lineId === lineId ? { ...item, quantity: item.quantity + 1 } : item)
    : [...activeCart.lines, { ...line, lineId, quantity: 1 }];
  return {
    ...activeCart,
    lines,
    quote: null,
    commerceCalculation: activeCart.commerceCalculation ?? commerceCalculation,
  };
}

export function applyLocalCartLineConfiguration(
  cart: LocalCartDraft,
  sourceLineId: string,
  line: LocalCartLineInput,
) {
  const sharedCart = currentRuntimeCartForDraft(cart);
  const sourceIndex = sharedCart.lines.findIndex((item) => item.lineId === sourceLineId);
  if (sourceIndex < 0) return sharedCart;

  const source = sharedCart.lines[sourceIndex];
  const replacementLineId = localCartLineId(line);
  if (replacementLineId === sourceLineId) return sharedCart;

  const target = sharedCart.lines.find((item) => item.lineId === replacementLineId);
  if (target) {
    return {
      ...sharedCart,
      lines: sharedCart.lines
        .filter((item) => item.lineId !== sourceLineId)
        .map((item) => item.lineId === replacementLineId
          ? { ...item, quantity: item.quantity + source.quantity }
          : item),
      quote: null,
    };
  }

  const lines = [...sharedCart.lines];
  lines[sourceIndex] = {
    ...line,
    lineId: replacementLineId,
    quantity: source.quantity,
  };
  return { ...sharedCart, lines, quote: null };
}

export function applyLocalCartLineQuantity(cart: LocalCartDraft, lineId: string, delta: number) {
  const sharedCart = currentRuntimeCartForDraft(cart);
  const lines = sharedCart.lines
    .map((line) => line.lineId === lineId ? { ...line, quantity: line.quantity + delta } : line)
    .filter((line) => line.quantity > 0);
  if (!lines.length) return resetEmptyCartContext(sharedCart);
  return { ...sharedCart, lines, quote: null };
}

export async function addLocalCartLine(
  identity: TrustedTerminalIdentity,
  cart: LocalCartDraft,
  line: LocalCartLineInput,
  commerceCalculation: CommerceCalculationRuntimeItem | null = null,
) {
  const next = applyLocalCartLine(cart, line, commerceCalculation);
  publishCurrentCartRuntime(identity, next);
  const saved = await saveLocalCart(identity, next);
  const current = currentRuntimeCart(identity);
  if (current === next) publishCurrentCartRuntime(identity, saved);
  return saved;
}

export async function changeLocalCartLineQuantity(identity: TrustedTerminalIdentity, cart: LocalCartDraft, lineId: string, delta: number) {
  const next = applyLocalCartLineQuantity(cart, lineId, delta);
  publishCurrentCartRuntime(identity, next);
  const saved = await saveLocalCart(identity, next);
  const current = currentRuntimeCart(identity);
  if (current === next) publishCurrentCartRuntime(identity, saved);
  return saved;
}

export async function setLocalCartMember(identity: TrustedTerminalIdentity, cart: LocalCartDraft, member: LocalSaleMember | null) {
  return mutateCurrentCart(identity, cart, (base) => ({ ...base, selectedMember: member, quote: null }));
}

export async function setLocalCartPromotionClaims(identity: TrustedTerminalIdentity, cart: LocalCartDraft, promotionVersionIds: string[]) {
  return mutateCurrentCart(identity, cart, (base) => ({ ...base, claimedPromotionVersionIds: [...new Set(promotionVersionIds)], quote: null }));
}

export async function setLocalCartQuote(identity: TrustedTerminalIdentity, cart: LocalCartDraft, quote: Omit<LocalSaleQuote, "cartFingerprint">) {
  const expectedRequestKey = cart.requestKey;
  const expectedFingerprint = localCartSaleFingerprint(cart);
  const currentAtStart = currentRuntimeCart(identity);
  if (
    currentAtStart && (
      currentAtStart.requestKey !== expectedRequestKey ||
      localCartSaleFingerprint(currentAtStart) !== expectedFingerprint
    )
  ) {
    throw new Error("POS_SALE_QUOTE_CART_CHANGED");
  }

  const token = captureCurrentCartRuntimeToken(identity);
  return queueCartPersistence(async () => {
    if (!isCurrentCartRuntimeTokenCurrent(identity, token)) {
      throw new Error("POS_SALE_QUOTE_CART_CHANGED");
    }

    const next = await posRuntimeDb.transaction("rw", posRuntimeDb.journal, async () => {
      const latest = await readLocalCart(identity);
      if (
        latest.requestKey !== expectedRequestKey ||
        localCartSaleFingerprint(latest) !== expectedFingerprint
      ) {
        throw new Error("POS_SALE_QUOTE_CART_CHANGED");
      }

      return persistLocalCartNow(identity, {
        ...latest,
        quote: { ...quote, cartFingerprint: expectedFingerprint },
      });
    });

    if (!publishCurrentCartRuntimeIfCurrent(identity, next, token)) {
      throw new Error("POS_SALE_QUOTE_CART_CHANGED");
    }
    return next;
  });
}
